import { useEffect, useState, useCallback } from "react";
import ProductImage from "@/components/ProductImage";
import { useCart, lineKey } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation, addressLocation } from "@/contexts/LocationContext";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, ShoppingBag, Loader2, MapPin, Plus, Star } from "lucide-react";
import { PaymentTrustBadge } from "@/components/TrustBadges";
import { AddressForm } from "@/components/AddressPicker";

import { logger } from "@/lib/logger";
import PhoneLogin from "@/components/PhoneLogin";
import { phoneLoginConfig } from "@/lib/firebasePhone";
import { BuyAgainButton } from "@/components/BuyAgain";
import { ContactCheck, mustConfirm } from "@/components/CustomerAuth";
export default function Cart() {
  const { items, removeItem, updateQty, clear, syncPrices, moveToShop, total, preselectedAddressId } = useCart();
  const { user } = useAuth();
  const { setLocation, addressId: headerAddressId } = useLocation();
  const navigate = useNavigate();

  // Saved-address flow (logged-in customers)
  const [addresses, setAddresses] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [confirming, setConfirming] = useState(null);   // saved address whose map pin must be confirmed
  const [loading, setLoading] = useState(false);
  const [deliveryChargeFlat, setDeliveryChargeFlat] = useState(0);
  const [deliveryFreeOver, setDeliveryFreeOver] = useState(0);
  // Phase 8 — server-side pricing preview (source of truth). Falls back to
  // FE math when the customer isn't logged in yet.
  const [preview, setPreview] = useState(null);
  // Phase 9 — customer-facing coupon input
  // A home banner can link here with ?coupon=CODE to fill in the code.
  const [searchParams] = useSearchParams();
  const [couponInput, setCouponInput] = useState(() => (searchParams.get("coupon") || "").trim().toUpperCase().slice(0, 32));
  const [appliedCoupon, setAppliedCoupon] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);
  // In a shop's last hour before closing the customer picks the delivery day.
  const [slot, setSlot] = useState("today");
  const [note, setNote] = useState(null);       // E-C2: this order's delivery note (null = not touched)

  useEffect(() => {
    api.get("/config/defaults")
      .then(({ data }) => {
        setDeliveryChargeFlat(Number(data?.delivery_charge_flat) || 0);
        setDeliveryFreeOver(Number(data?.delivery_free_over) || 0);
      })
      .catch((e) => { logger.warn("Failed to load /config/defaults:", e); });
  }, []);

  // G6: refresh prices/availability from the server when the cart opens
  // (guests included) so a saved cart never shows yesterday's prices.
  const offerKey = items.map(i => i.offer_id).filter(Boolean).sort().join(",");
  useEffect(() => {
    if (!offerKey) return;
    api.post("/cart/prices", { offer_ids: offerKey.split(",") })
      .then(({ data }) => {
        const byOffer = Object.fromEntries((data.offers || []).map(o => [o.offer_id, o]));
        const changed = syncPrices(byOffer);
        if (changed) toast.info("Some prices or availability in your cart have changed.");
      })
      .catch((e) => logger.warn("Cart price refresh failed:", e));
  }, [offerKey, syncPrices]);

  // Phase 8 — refresh server-side cart preview whenever the cart changes.
  useEffect(() => {
    if (!user || items.length === 0) { setPreview(null); return; }
    const payload = { items: items.filter(i => i.offer_id).map(i => ({ offer_id: i.offer_id, quantity: i.quantity })) };
    if (payload.items.length === 0) { setPreview(null); return; }
    if (appliedCoupon) payload.coupon_code = appliedCoupon;
    if (selectedId) payload.address_id = selectedId;   // real fee by distance + each shop's delivery day
    payload.delivery_slot = slot;
    api.post("/cart/preview", payload)
      .then(({ data }) => {
        setPreview(data);
        if (appliedCoupon && data.coupon_error) {
          toast.error(_couponError(data.coupon_error));
          setAppliedCoupon("");
        }
      })
      .catch(() => setPreview(null));
  }, [user, items, appliedCoupon, selectedId, slot]);

  const _couponError = (reason) => ({
    not_found: "Coupon code not recognised",
    inactive: "Coupon is no longer active",
    expired: "Coupon has expired",
    not_yet_valid: "Coupon is not yet valid",
    usage_exhausted: "Coupon usage limit reached",
    per_customer_exhausted: "You've already used this coupon",
    min_cart_not_met: "Cart value is too low for this coupon",
    not_eligible: "Coupon is not valid for this order",
    empty: "Please enter a coupon code",
  }[reason] || "Coupon could not be applied");

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setCouponBusy(true);
    try {
      const payload = {
        code,
        items: items.filter(i => i.offer_id).map(i => ({ offer_id: i.offer_id, quantity: i.quantity })),
      };
      const { data } = await api.post("/customer/coupons/validate", payload);
      if (!data.ok) {
        toast.error(_couponError(data.reason));
        return;
      }
      setAppliedCoupon(code);
      toast.success(`Coupon ${code} applied · you save ₹${data.discount_amount}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Coupon could not be applied");
    } finally { setCouponBusy(false); }
  };

  const removeCoupon = () => {
    setAppliedCoupon("");
    setCouponInput("");
    toast.success("Coupon removed");
  };

  const loadAddresses = useCallback(async () => {
    if (!user || user.role !== "customer") return;
    try {
      const { data } = await api.get("/customer/addresses");
      setAddresses(data);
      const preferred = (preselectedAddressId && data.find(a => a.id === preselectedAddressId))
        || (headerAddressId && data.find(a => a.id === headerAddressId))
        || data.find(a => a.is_default)
        || data[0];
      // Only auto-select if the user hasn't already picked one — check against
      // the latest state via the setter to avoid needing selectedId in deps
      // (which would refetch on every selection change).
      if (preferred) setSelectedId(prev => prev || preferred.id);
    } catch (e) { logger.warn("Failed to load customer addresses:", e); }
  }, [user, preselectedAddressId, headerAddressId]);
  useEffect(() => { loadAddresses(); }, [loadAddresses]);

  const byVendor = items.reduce((acc, i) => {
    (acc[i.vendor_id] = acc[i.vendor_id] || { vendor_id: i.vendor_id, vendor_name: i.vendor_name, items: [] }).items.push(i);
    return acc;
  }, {});

  const shopCount = Math.max(1, Object.keys(byVendor).length);

  // E-C2: the old per-address instructions start the note, until the customer changes it.
  const noteText = note ?? (addresses.find(a => a.id === selectedId)?.instructions || "");

  const checkout = async () => {
    if (!user) { navigate("/login?next=/cart"); return; }
    if (!selectedId) {
      if (addresses.length === 0) setAddDialogOpen(true);
      return toast.error(addresses.length ? "Please pick a delivery address" : "Add a delivery address to continue");
    }
    const sel = addresses.find(a => a.id === selectedId);
    if (sel?.needs_location) {
      setConfirming(sel);
      return toast.info("Confirm this address on the map first so the rider can find you.");
    }
    if (sel?.serviceability_status === "not_covered") {
      return toast.error("No shop delivers to this address yet. Pick another address.");
    }
    setLoading(true);
    // The server prices the whole cart (every shop), charges that amount, and
    // then creates all shop orders in one call from what was paid for.
    if (items.some(i => i.unavailable)) {
      setLoading(false);
      return toast.error("Remove the items that are no longer available before checking out.");
    }
    const cartItems = items.map(i => ({ offer_id: i.offer_id, quantity: i.quantity }));
    if (cartItems.some(i => !i.offer_id)) {
      setLoading(false);
      return toast.error("Some items in your cart are out of date. Please remove them and add them again.");
    }
    const shortShop = (preview?.shops || []).find(s => s.min_order_shortfall > 0);
    if (shortShop) {
      setLoading(false);
      return toast.error(`Add ₹${shortShop.min_order_shortfall.toFixed(0)} more from ${shortShop.vendor_name} — that's the minimum order.`);
    }
    const addressPayload = { address_id: selectedId, delivery_slot: slot, delivery_note: noteText };
    const couponPayload = appliedCoupon ? { coupon_code: appliedCoupon } : {};
    let paid = false;
    try {
      const { data: cfg } = await api.get("/payments/config");
      if (cfg?.enabled) {
        if (typeof window.Razorpay !== "function") {
          throw new Error("The payment window couldn't load. Please check your connection and try again.");
        }
        const { data: rzpOrder } = await api.post("/payments/razorpay/create-order", {
          items: cartItems, ...addressPayload, ...couponPayload, currency: "INR",
        });
        const paidCtx = await new Promise((resolve, reject) => {
          const options = {
            key: rzpOrder.key_id,
            amount: rzpOrder.amount,
            currency: rzpOrder.currency,
            order_id: rzpOrder.razorpay_order_id,
            name: "PawBazaar",
            description: "Order payment",
            prefill: { name: user.name || "", email: user.email || "", contact: user.phone || "" },
            theme: { color: "#ea9739" },
            handler: (res) => resolve(res),
            modal: {
              ondismiss: () => reject(new Error("Payment cancelled")),
            },
          };
          const rzp = new window.Razorpay(options);
          rzp.on("payment.failed", (resp) => reject(new Error(resp?.error?.description || "Payment failed")));
          rzp.open();
        });
        const rzpCtx = {
          razorpay_order_id: paidCtx.razorpay_order_id,
          razorpay_payment_id: paidCtx.razorpay_payment_id,
          razorpay_signature: paidCtx.razorpay_signature,
        };
        await api.post("/payments/razorpay/verify", rzpCtx);
        paid = true;
        await api.post("/orders/checkout", rzpCtx);
      } else if (cfg?.mock_enabled) {
        // Development servers only (ALLOW_MOCK_PAYMENTS=true).
        await api.post("/orders/checkout", { items: cartItems, ...addressPayload, ...couponPayload });
      } else {
        throw new Error("Online payment is unavailable right now. Please try again later.");
      }
      if (sel) setLocation(addressLocation(sel));
      clear();
      toast.success("Order placed! The shop has been notified.");
      navigate("/account");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === "address_needs_location") {
        const a = addresses.find(x => x.id === detail.address_id) || sel;
        if (a) setConfirming(a);
      }
      const msg = err?.message === "Payment cancelled" ? "Payment cancelled" : (formatApiErrorDetail(err.response?.data?.detail) || err.message);
      if (paid) {
        // Money was taken but the orders weren't created. The payment is
        // flagged for refund on the server; tell the customer plainly.
        toast.error(`${msg} Your payment was received and will be refunded if the order isn't placed — contact support if you have questions.`, { duration: 10000 });
      } else {
        toast.error(msg);
      }
    } finally { setLoading(false); }
  };

  if (items.length === 0) return <EmptyCart signedIn={user?.role === "customer"} />;

  const useSaved = !!user && addresses.length > 0;
  const selectedAddr = addresses.find(a => a.id === selectedId);
  const addrBlocked = !!(selectedAddr && !selectedAddr.needs_location && selectedAddr.serviceability_status === "not_covered");

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-stone-500 mb-2">Checkout</div>
        <h1 className="font-heading text-4xl font-semibold tracking-tight">Your cart</h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {preview?.one_shop_option && (
            <div className="rounded-2xl border border-teal/40 bg-teal/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="cart-one-shop">
              <div className="flex-1 text-sm">
                <div className="font-semibold">Get everything from {preview.one_shop_option.vendor_name} in one delivery</div>
                <div className="text-stone-600 text-xs mt-0.5">They have every item in your cart. One delivery instead of {shopCount}, so you pay one delivery fee.</div>
              </div>
              <Button size="sm" data-testid="cart-one-shop-switch" className="rounded-full bg-teal text-white hover:bg-teal/90"
                onClick={() => { moveToShop(preview.one_shop_option.lines, preview.one_shop_option); toast.success(`Your cart is now from ${preview.one_shop_option.vendor_name}`); }}>
                Switch
              </Button>
            </div>
          )}
          {shopCount > 1 && !preview?.one_shop_option && (
            <div className="text-xs text-stone-600 rounded-xl bg-stone-50 border border-stone-200 px-4 py-2" data-testid="cart-multi-shop-note">
              Your cart is from {shopCount} shops, so it comes in {shopCount} separate deliveries (each shop's delivery fee is shown below) and may arrive at different times.
            </div>
          )}
          {(preview?.shops || []).some(s => s.delivery?.mode === "choice") && (
            <Card className="p-4 rounded-2xl border-marigold/40 bg-marigold/5" data-testid="cart-slot-choice">
              <div className="text-sm font-semibold">When should it come?</div>
              <div className="text-xs text-stone-600 mt-0.5">{(preview.shops.find(s => s.delivery?.mode === "choice") || {}).delivery?.label}</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {[["today", "Today"], ["next_open", `Next opening (${(preview.shops.find(s => s.delivery?.mode === "choice") || {}).delivery?.next_open_label || "tomorrow"})`]].map(([v, label]) => (
                  <button key={v} type="button" onClick={() => setSlot(v)} data-testid={`cart-slot-${v}`}
                    className={`rounded-full px-4 py-2 text-sm border ${slot === v ? "bg-stoneWarm text-white border-stoneWarm" : "bg-white border-stone-300 text-stone-700"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </Card>
          )}
          {Object.values(byVendor).map(g => (
            <Card key={g.vendor_id} className="rounded-2xl border-stone-200 overflow-hidden">
              <div className="px-5 py-3 bg-stone-50 border-b border-stone-200">
                <div className="text-xs uppercase tracking-widest font-bold text-stone-500">{g.vendor_name}</div>
                <ShopDelivery shop={(preview?.shops || []).find(s => s.vendor_id === g.vendor_id)} />
              </div>
              <div className="divide-y divide-stone-200">
                {g.items.map(i => (
                  <div key={lineKey(i)} data-testid={`cart-item-${i.product_id}`} className="p-4 flex flex-wrap sm:flex-nowrap items-center gap-x-4 gap-y-2">
                    <ProductImage src={i.image_url} alt={i.name} className="h-16 w-16 shrink-0 rounded-xl object-cover bg-stone-100" iconClass="h-6 w-6" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{i.name}</div>
                      <div className="text-xs text-stone-500 mt-0.5">₹{i.price} each</div>
                      {preview?.offer_lines?.[i.offer_id]?.note && (
                        <div className="text-xs font-semibold text-stoneWarm mt-0.5" data-testid={`cart-offer-${i.product_id}`}>
                          {preview.offer_lines[i.offer_id].free_units ? `${preview.offer_lines[i.offer_id].free_units} free: ` : ""}{preview.offer_lines[i.offer_id].note}</div>
                      )}
                      {i.unavailable && <div className="text-xs text-terracotta font-semibold mt-0.5" data-testid={`cart-unavailable-${i.product_id}`}>No longer available — please remove</div>}
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto pl-20 sm:pl-0">
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="icon" onClick={() => updateQty(lineKey(i), i.quantity-1)} data-testid={`cart-dec-${i.product_id}`} className="h-8 w-8 rounded-full">-</Button>
                      <div className="w-8 text-center text-sm font-semibold">{i.quantity}</div>
                      <Button variant="outline" size="icon" onClick={() => updateQty(lineKey(i), i.quantity+1)} data-testid={`cart-inc-${i.product_id}`} className="h-8 w-8 rounded-full">+</Button>
                    </div>
                    <div className="ml-auto sm:ml-0 w-20 text-right font-heading font-bold">
                      {preview?.offer_lines?.[i.offer_id]?.discount ? <>
                        {i.price * i.quantity - preview.offer_lines[i.offer_id].discount <= 0 ? <span className="text-stoneWarm">Free</span>
                          : `₹${(i.price * i.quantity - preview.offer_lines[i.offer_id].discount).toFixed(0)}`}
                        <div className="text-xs font-normal text-stone-400 line-through">₹{(i.price*i.quantity).toFixed(0)}</div>
                      </> : `₹${(i.price*i.quantity).toFixed(0)}`}</div>
                    <Button variant="ghost" size="icon" onClick={() => removeItem(lineKey(i))} data-testid={`cart-remove-${i.product_id}`} aria-label="Remove">
                      <Trash2 className="h-4 w-4 text-stone-500" />
                    </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>

        <div className="space-y-4">
          <Card className="p-5 rounded-2xl border-stone-200 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-heading font-semibold text-lg flex items-center gap-2"><MapPin className="h-4 w-4 text-marigold" /> Delivery address</div>
              {user && (
                <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="ghost" data-testid="cart-add-address-btn" className="text-xs text-teal">
                      <Plus className="h-3 w-3 mr-1" /> New
                    </Button>
                  </DialogTrigger>
                  {addDialogOpen && (
                    <AddressForm testidPrefix="cart-new-addr"
                      onDone={async (a) => { setAddDialogOpen(false); await loadAddresses(); if (a) setSelectedId(a.id); }} />
                  )}
                </Dialog>
              )}
              <Dialog open={!!confirming} onOpenChange={(o) => !o && setConfirming(null)}>
                {confirming && (
                  <AddressForm editing={confirming} testidPrefix="cart-confirm-addr"
                    onDone={async (a) => { setConfirming(null); await loadAddresses(); if (a) setSelectedId(a.id); }} />
                )}
              </Dialog>
            </div>

            {useSaved ? (
              <div className="space-y-2" data-testid="cart-saved-addresses">
                <Select value={selectedId || ""} onValueChange={setSelectedId}>
                  <SelectTrigger className="h-12 w-full min-w-0" data-testid="cart-address-select">
                    <SelectValue placeholder="Choose a delivery address" />
                  </SelectTrigger>
                  <SelectContent className="max-w-[calc(100vw-2rem)]">
                    {addresses.map(a => (
                      <SelectItem key={a.id} value={a.id} data-testid={`cart-address-option-${a.id}`}>
                        <span className="font-semibold">{a.label}</span>
                        <span className="text-xs text-stone-500"> — {[a.flat_no, a.address].filter(Boolean).join(", ").slice(0, 50)}</span>
                        {a.needs_location ? <span className="text-[10px] text-marigold font-semibold"> · confirm pin</span>
                          : a.serviceability_status !== "active" ? <span className="text-[10px] text-terracotta font-semibold"> · not delivered</span> : null}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedAddr && (
                  <div className="rounded-xl border border-stone-200 p-3 text-sm min-w-0" data-testid="cart-selected-address">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="border-0 rounded-full text-[10px] bg-stone-200 text-stone-700">{selectedAddr.label}</Badge>
                      {selectedAddr.is_default && <Star className="h-3 w-3 text-teal fill-teal" />}
                      {selectedAddr.needs_location ? (
                        <Badge data-testid={`cart-addr-svc-${selectedAddr.id}`} className="bg-marigold/15 text-marigold border-0 rounded-full text-[10px]">Confirm pin</Badge>
                      ) : selectedAddr.serviceability_status === "active" ? (
                        <Badge data-testid={`cart-addr-svc-${selectedAddr.id}`} className="bg-teal/15 text-teal border-0 rounded-full text-[10px]">Serviceable</Badge>
                      ) : (
                        <Badge data-testid={`cart-addr-svc-${selectedAddr.id}`} className="bg-terracotta/15 text-terracotta border-0 rounded-full text-[10px]">Not delivered</Badge>
                      )}
                    </div>
                    <div className="mt-1 text-stone-700 break-words">{[selectedAddr.flat_no, selectedAddr.address].filter(Boolean).join(", ")}</div>
                    <div className="text-xs text-stone-500">{[selectedAddr.landmark && `Near ${selectedAddr.landmark}`, selectedAddr.city, selectedAddr.pincode].filter(Boolean).join(" · ")}</div>
                  </div>
                )}
                {selectedAddr?.needs_location && (
                  <button type="button" onClick={() => setConfirming(selectedAddr)} data-testid="cart-address-confirm-pin"
                    className="w-full text-left rounded-xl border border-marigold/40 bg-marigold/10 p-3 text-xs text-stoneWarm">
                    <div className="font-semibold">Confirm this address on the map</div>
                    <div className="mt-0.5">We need the exact spot so the rider can find you. Tap to set the pin.</div>
                  </button>
                )}
                {addrBlocked && (
                  <div data-testid="cart-address-not-serviceable" className="rounded-xl border border-terracotta/40 bg-terracotta/5 p-3 text-xs text-terracotta">
                    <div className="font-semibold">No shop delivers to this address yet</div>
                    <div className="mt-0.5">Pick another address, or add a new one.</div>
                  </div>
                )}
                <div className="text-[10px] text-stone-500 text-center pt-1">
                  Manage all addresses in <a href="/account?tab=profile&section=addresses" className="text-teal hover:underline">your profile</a>.
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-marigold/40 bg-marigold/5 p-4 text-sm text-stone-600" data-testid="cart-no-address">
                {user ? (
                  <>
                    Add your delivery address — search for it and drop the pin on your door.
                    <div className="mt-3">
                      <Button size="sm" onClick={() => setAddDialogOpen(true)} data-testid="cart-add-first-address" className="rounded-full bg-marigold text-stoneWarm hover:bg-marigold/90 font-semibold">
                        <Plus className="h-3 w-3 mr-1" /> Add address
                      </Button>
                    </div>
                  </>
                ) : "Log in at checkout to choose your delivery address."}
              </div>
            )}
          </Card>

          {user && <DeliveryNote value={noteText} onChange={setNote} />}

          <Card className="p-5 rounded-2xl border-stone-200">
            <div className="font-heading font-semibold text-lg mb-3">Summary</div>
            <div className="flex justify-between text-sm mb-2" data-testid="cart-summary-subtotal"><span>Subtotal</span><span>₹{(preview?.merchandise_subtotal ?? total).toFixed(0)}</span></div>
            {preview?.bundle_discount > 0 && (
              <div className="flex justify-between text-sm mb-2 text-teal" data-testid="cart-summary-shop-offers">
                <span>Shop offers (free gifts and combos)</span><span>−₹{preview.bundle_discount.toFixed(0)}</span>
              </div>
            )}
            {(preview?.discount_total ?? preview?.pawbazaar_discount) > 0 && (
              <div className="flex justify-between text-sm mb-2 text-teal" data-testid="cart-summary-pb-discount">
                <span>{["PAWBAZAAR_COUPON", "SHOP_COUPON"].includes(preview.pawbazaar_discount_source) ? `Coupon (${preview.coupon_code})` : "PawBazaar discount"}</span>
                <span>−₹{(preview.discount_total ?? preview.pawbazaar_discount).toFixed(0)}</span>
              </div>
            )}
            {/* Phase 9 — Coupon input */}
            {user && items.length > 0 && (
              <div className="mb-3 pb-3 border-b border-stone-100" data-testid="cart-coupon-section">
                {appliedCoupon ? (
                  <div className="flex items-center justify-between rounded-xl border border-teal/40 bg-teal/5 px-3 py-2 text-sm" data-testid="cart-coupon-applied">
                    <div>
                      <span className="font-semibold text-teal">{appliedCoupon}</span>
                      <span className="text-stone-500 ml-2 text-xs">applied</span>
                    </div>
                    <button onClick={removeCoupon} className="text-xs text-stone-500 hover:text-stone-800" data-testid="cart-coupon-remove">Remove</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input data-testid="cart-coupon-input" value={couponInput} onChange={e => setCouponInput(e.target.value.toUpperCase())} placeholder="Coupon code" className="h-10 rounded-full text-sm uppercase tracking-wide" />
                    <Button size="sm" onClick={applyCoupon} disabled={couponBusy || !couponInput.trim()} data-testid="cart-coupon-apply" className="rounded-full h-10 px-4 bg-stoneWarm text-white hover:bg-stoneWarm/90">
                      {couponBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                    </Button>
                  </div>
                )}
              </div>
            )}
            {(() => {
              const previewMode = preview !== null;
              const merchandise = previewMode ? preview.merchandise_subtotal : total;
              const effectiveValue = previewMode ? preview.effective_delivery_value : total;
              // Each shop ships (and charges delivery) separately.
              const effective = previewMode ? preview.delivery_fee : ((deliveryFreeOver > 0 && total >= deliveryFreeOver) ? 0 : deliveryChargeFlat * shopCount);
              const waived = previewMode ? preview.delivery_fee_waived : (deliveryFreeOver > 0 && total >= deliveryFreeOver);
              const threshold = previewMode ? preview.free_delivery_threshold : deliveryFreeOver;
              const grand = previewMode ? preview.customer_payable : (total + effective);
              return (
                <>
                  <div className="flex justify-between text-sm mb-2 text-stone-600" data-testid="cart-summary-delivery">
                    <span>Delivery charge{shopCount > 1 ? ` (${shopCount} shops)` : ""}</span>
                    <span>
                      {effective > 0 ? (
                        `₹${effective.toFixed(0)}`
                      ) : deliveryChargeFlat > 0 && waived ? (
                        <span className="text-emerald-600 font-medium">
                          <span className="line-through text-stone-400 mr-1.5">₹{deliveryChargeFlat.toFixed(0)}</span>FREE
                        </span>
                      ) : (
                        "Included"
                      )}
                    </span>
                  </div>
                  {!waived && threshold > 0 && deliveryChargeFlat > 0 && (
                    <div className="mb-3" data-testid="cart-summary-free-hint">
                      <div className="flex items-center justify-between text-xs text-marigold mb-1">
                        <span>Add ₹{Math.max(0, threshold - effectiveValue).toFixed(0)} more for free delivery</span>
                        <span className="text-stone-500">₹{threshold.toFixed(0)}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-stone-200 overflow-hidden" data-testid="cart-free-delivery-progress">
                        <div
                          className="h-full bg-marigold transition-all duration-500"
                          style={{ width: `${Math.max(4, Math.min(100, (effectiveValue / threshold) * 100))}%` }}
                        />
                      </div>
                    </div>
                  )}
                  {previewMode && preview.platform_fee > 0 && (
                    <div className="flex justify-between text-sm mb-2 text-stone-600" data-testid="cart-summary-platform-fee">
                      <span title="A small fee that helps us run PawBazaar: secure online payments, maps and order updates.">Platform fee</span>
                      <span>₹{preview.platform_fee.toFixed(0)}</span>
                    </div>
                  )}
                  <div className="border-t border-stone-200 mt-3 pt-3 flex justify-between font-heading text-lg font-bold" data-testid="cart-summary-total">
                    <span>Total</span><span>₹{grand.toFixed(0)}</span>
                  </div>
                </>
              );
            })()}
            {!user ? <GuestCheckout /> : (() => {
              const blocked = addrBlocked;
              return (<>
                <div className="mt-4"><ContactCheck checkout /></div>
                <Button disabled={loading || blocked || mustConfirm(user)} onClick={checkout} data-testid="cart-checkout-btn" className="w-full mt-4 h-12 rounded-full bg-marigold text-stoneWarm hover:bg-marigold/90 font-semibold">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : blocked ? "Address not serviceable" : `Pay ₹${(preview?.customer_payable ?? (total + (deliveryFreeOver > 0 && total >= deliveryFreeOver ? 0 : deliveryChargeFlat * shopCount))).toFixed(0)} · Place order`}
                </Button>
              </>);
            })()}
            <div className="mt-3 flex justify-center">
              <PaymentTrustBadge testid="cart-payment-trust-badge" />
            </div>
            <p className="text-[10px] text-stone-500 mt-2 text-center">Secure checkout powered by Razorpay · UPI, Cards and NetBanking accepted</p>
          </Card>
        </div>
      </div>
    </div>
  );
}


// Delivery day for one shop (from its opening hours), plus the minimum order
// and "doesn't deliver here" warnings from the server preview.
function ShopDelivery({ shop }) {
  if (!shop) return null;
  const d = shop.delivery;
  return (
    <div className="mt-1 space-y-1" data-testid={`cart-shop-delivery-${shop.vendor_id}`}>
      {d && (
        <div className={`text-xs ${d.mode === "scheduled" ? "text-marigold font-semibold" : d.mode === "unavailable" ? "text-terracotta font-semibold" : "text-stone-600"}`}>
          {d.mode === "scheduled" ? d.label : d.mode === "unavailable" ? `${d.label} — remove its items` : d.promise}
          {shop.delivery_fee > 0 ? ` · Delivery ₹${shop.delivery_fee.toFixed(0)}` : ""}
          {shop.distance_km != null ? ` · ${shop.distance_km} km away` : ""}
        </div>
      )}
      {shop.min_order_shortfall > 0 && (
        <div className="text-xs text-terracotta font-semibold" data-testid={`cart-min-order-${shop.vendor_id}`}>
          Add ₹{shop.min_order_shortfall.toFixed(0)} more from this shop — minimum order
        </div>
      )}
      {shop.not_delivering && <div className="text-xs text-terracotta font-semibold">{shop.not_delivering}</div>}
    </div>
  );
}

const NOTE_CHIPS = ["Don't ring the bell", "Leave at the door", "Dog at home", "Call when you arrive", "Leave with security"];

/** E-C2: a note for this order's delivery, with one-tap chips. */
function DeliveryNote({ value, onChange }) {
  const has = (c) => value.split(/\s*·\s*/).includes(c);
  const toggle = (c) => {
    const parts = value.split(/\s*·\s*/).filter(Boolean);
    onChange((has(c) ? parts.filter((p) => p !== c) : [...parts, c]).join(" · ").slice(0, 300));
  };
  return (
    <Card className="p-5 rounded-2xl border-stone-200 space-y-3" data-testid="cart-delivery-note">
      <div className="font-heading font-semibold">Note for the delivery <span className="text-stone-400 font-normal text-sm">(optional)</span></div>
      <div className="flex flex-wrap gap-2">
        {NOTE_CHIPS.map((c) => (
          <button type="button" key={c} onClick={() => toggle(c)} data-testid={`cart-note-chip-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`min-h-[36px] px-3 rounded-full text-sm border ${has(c) ? "bg-teal text-white border-teal" : "border-stone-300 text-stone-700"}`}>{c}</button>
        ))}
      </div>
      <Textarea value={value} maxLength={300} rows={2} onChange={(e) => onChange(e.target.value)} placeholder="Anything else the rider should know"
        data-testid="cart-note-text" />
      <p className="text-xs text-stone-500">For this order only. The shop and the rider see it.</p>
    </Card>
  );
}

/** A guest's cart: sign in (or create an account) with a mobile code right
 *  here, and the cart stays as it is. Email sign-in is on the sign-in page. */
function GuestCheckout() {
  const { loginWith, refresh } = useAuth();
  const [phone, setPhone] = useState(null);
  useEffect(() => { phoneLoginConfig().then((c) => setPhone(!!c.enabled), () => setPhone(false)); }, []);
  const signedIn = async (data) => {
    loginWith(data.token, data.user);
    await refresh();
    toast.success(`Signed in. Choose where to deliver, then pay.`);
  };
  return (
    <div className="mt-4 rounded-2xl bg-teal/5 border border-teal/20 p-4 space-y-3" data-testid="cart-guest-checkout">
      <div className="font-semibold">Sign in to check out</div>
      {phone === null ? <Loader2 className="h-4 w-4 animate-spin" /> : phone ? (
        <>
          <PhoneLogin onSignedIn={signedIn} />
          <p className="text-xs text-stone-600">Your cart stays as it is. New here? The same code creates your account.</p>
          <Link to="/login?next=/cart&method=email" className="block text-center text-sm font-semibold text-teal hover:underline">Sign in with email instead</Link>
        </>
      ) : (
        <>
          <p className="text-sm text-stone-600">Your cart stays as it is while you sign in.</p>
          <Link to="/login?next=/cart" data-testid="cart-guest-signin" className="flex items-center justify-center h-12 rounded-full bg-stoneWarm text-white font-semibold hover:bg-stoneWarm/90">Sign in to check out</Link>
          <Link to="/register?next=/cart" className="block text-center text-sm font-semibold text-teal hover:underline">New here? Create an account</Link>
        </>
      )}
    </div>
  );
}

/** Nothing in the cart: the last orders to buy again (signed in), or a way to start. */
function EmptyCart({ signedIn }) {
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    if (!signedIn) return;
    api.get("/customer/orders").then(({ data }) => setOrders((Array.isArray(data) ? data : data?.items || [])
      .filter((o) => o.status === "delivered").slice(0, 3)), () => setOrders([]));
  }, [signedIn]);
  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-12" data-testid="cart-empty">
      <div className="text-center">
        <ShoppingBag className="h-12 w-12 mx-auto text-stone-400" />
        <h1 className="font-heading text-3xl font-semibold mt-4">Your cart is empty</h1>
      </div>
      {orders.length > 0 && (
        <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-4 space-y-3" data-testid="cart-buy-again">
          <div className="font-semibold">Buy again from your last orders</div>
          {orders.map((o) => (
            <div key={o.id} className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{(o.items || []).map((i) => i.name).filter(Boolean).slice(0, 2).join(", ") || "Order"}{(o.items || []).length > 2 ? ` +${o.items.length - 2} more` : ""}</div>
                <div className="text-xs text-stone-500">{o.vendor_name || o.vendor?.shop_name || ""}{o.created_at ? ` · ${new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}` : ""}</div>
              </div>
              <BuyAgainButton orderId={o.id} />
            </div>
          ))}
        </div>
      )}
      <div className="text-center mt-6">
        <Link to="/shop" data-testid="cart-empty-browse-btn" className="inline-flex items-center rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90 h-12 px-6 font-semibold">Browse products</Link>
      </div>
    </div>
  );
}
